import { createSlice } from '@reduxjs/toolkit';

import { ORDER_STATUS, SOCKET_TOPICS } from 'src/common/constants';
import { errorHandler } from 'src/common/helpers';
import {
    SpotAsset,
    Toast,
    Symbol,
    ActiveOrder,
    ConditionalOrder,
    TradeOrder,
    MyTrade,
    TokenInfo,
    SpotBalance,
    SpotOrderSetting,
} from 'src/types';

export const callTypes = {
    list: 'list',
    action: 'action',
};

interface SpotPageState {
    loading: boolean;
    error?: string;
    currentSymbol: Symbol;
    assets: SpotAsset[];
    orderCreated: number;
    createOrderPrice: string;
    tvWidgetRef: any;
    tokenInfo: TokenInfo;
    toast: Toast;
    activeOpenOrders: ActiveOrder[];
    conditionalOpenOrders: ConditionalOrder[];
    activeTradeOrders: TradeOrder[];
    conditionalTradeOrders: TradeOrder[];
    tradeOrderHistory: TradeOrder[];
    myTrades: MyTrade[];
    orderSetting: SpotOrderSetting;
}

const initialState = {
    currentSymbol: {} as Symbol,
    assets: [] as SpotAsset[],
    createOrderPrice: '',
    tvWidgetRef: null,
    tokenInfo: {} as TokenInfo,
    toast: {} as Toast,
    activeOpenOrders: [] as ActiveOrder[],
    conditionalOpenOrders: [] as ConditionalOrder[],
    activeTradeOrders: [] as TradeOrder[],
    conditionalTradeOrders: [] as TradeOrder[],
    tradeOrderHistory: [] as TradeOrder[],
    myTrades: [] as MyTrade[],
    orderSetting: {
        isConfirm: true,
    },
    orderCreated: 0,
} as SpotPageState;

export const spotPageSlice = createSlice({
    name: 'spotPage',
    initialState,
    reducers: {
        // eslint-disable-next-line no-unused-vars
        startCall: (state, action) => {
            state.error = undefined;
            state.loading = true;
        },
        // eslint-disable-next-line no-unused-vars
        callEnded: (state, action) => {
            state.loading = false;
        },
        catchError: (state, action) => {
            state.loading = false;
            const error = errorHandler(action.payload);
            if (error) {
                state.error = error.title;
                state.toast = error;
            }
        },
        saveCurrentSymbol: (state, action) => {
            state.currentSymbol = action.payload;
            localStorage.setItem('currentSpotSymbol', action.payload.symbolId);
        },
        // eslint-disable-next-line no-unused-vars
        orderCreated: (state, action) => {
            state.loading = false;
            state.orderCreated = ++state.orderCreated;
            state.toast = {
                id: new Date().getTime().toString(),
                title: 'order_successful',
                type: 'success',
            };
        },
        availableAssetsFetched: (state, action) => {
            state.assets = action.payload;
        },
        updateTvWidgetRef(state, action) {
            state.tvWidgetRef = action.payload;
        },
        updateCreateOrderPrice(state, action) {
            state.createOrderPrice = action.payload;
        },
        tokenInfoFetched(state, action) {
            state.tokenInfo = action.payload.data;
        },
        showNotification(state, action) {
            state.toast = {
                id: new Date().getTime().toString(),
                title: action.payload.title,
                type: action.payload.type,
                description: action.payload.description,
            };
        },
        saveActiveOpenOrders(state, action) {
            state.activeOpenOrders = action.payload;
            state.loading = false;
        },
        saveConditionalOpenOrders(state, action) {
            state.conditionalOpenOrders = action.payload;
            state.loading = false;
        },
        saveActiveTradeOrders(state, action) {
            state.activeTradeOrders = action.payload;
            state.tradeOrderHistory = [
                ...state.activeTradeOrders,
                ...state.conditionalTradeOrders,
            ].sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
            state.loading = false;
        },
        saveConditionalTradeOrders(state, action) {
            state.conditionalTradeOrders = action.payload;
            state.tradeOrderHistory = [
                ...state.activeTradeOrders,
                ...state.conditionalTradeOrders,
            ].sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
            state.loading = false;
        },
        saveMyTrades(state, action) {
            state.myTrades = action.payload;
            state.loading = false;
        },
        activeOrderCanceled: (state, action) => {
            state.loading = false;
            const canceledOrder = action.payload;
            if (canceledOrder.success) {
                state.activeOpenOrders = state.activeOpenOrders.filter(
                    (order: ActiveOrder) => order.orderId !== canceledOrder.orderId,
                );
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'order_cancelled',
                    type: 'success',
                };
            } else {
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'order_cancelled_failure',
                    type: 'error',
                };
            }
        },
        conditionalOrderCanceled: (state, action) => {
            state.loading = false;
            const canceledOrder = action.payload;
            if (canceledOrder.success) {
                state.conditionalOpenOrders = state.conditionalOpenOrders.filter(
                    (order: ConditionalOrder) => order.orderId !== canceledOrder.orderId,
                );
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'order_cancelled',
                    type: 'success',
                };
            } else {
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'order_cancelled_failure',
                    type: 'error',
                };
            }
        },
        socketUserDataReceived: (state, action) => {
            const { data, topic } = action.payload;
            if (data) {
                switch (topic) {
                    case SOCKET_TOPICS.ORDER: // limit orders
                        data.map((order: ActiveOrder) => {
                            if (
                                order.status === ORDER_STATUS.NEW ||
                                order.status === ORDER_STATUS.PARTIALLY_FILLED
                            ) {
                                const orders = filterOrders(order, state.activeOpenOrders);
                                state.activeOpenOrders = orders as ActiveOrder[];
                            } else if (
                                order.status === ORDER_STATUS.FILLED ||
                                order.status === ORDER_STATUS.CANCELED
                            ) {
                                state.activeOpenOrders = state.activeOpenOrders.filter(
                                    (currentOrder) => currentOrder.orderId !== order.orderId,
                                );
                            }
                            state.tradeOrderHistory = filterOrders(order, state.tradeOrderHistory);
                            state.activeOpenOrders.sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
                            state.tradeOrderHistory.sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
                            if (
                                order.status === ORDER_STATUS.PARTIALLY_FILLED ||
                                order.status === ORDER_STATUS.FILLED
                            ) {
                                state.toast = {
                                    id: order.clientOrderId,
                                    title:
                                        order.status === ORDER_STATUS.FILLED
                                            ? 'order_filled'
                                            : 'order_partially_filled',
                                    type: 'info',
                                };
                            }
                        });

                        break;
                    case SOCKET_TOPICS.PLAN_ORDER: // conditional orders
                        data.map((order: ConditionalOrder) => {
                            if (order.status === ORDER_STATUS.ORDER_NEW) {
                                const orders = filterOrders(order, state.conditionalOpenOrders);
                                state.conditionalOpenOrders = orders as ConditionalOrder[];
                            } else if (
                                order.status === ORDER_STATUS.ORDER_FILLED ||
                                order.status === ORDER_STATUS.ORDER_CANCELED
                            ) {
                                state.conditionalOpenOrders = state.conditionalOpenOrders.filter(
                                    (currentOrder) => currentOrder.orderId !== order.orderId,
                                );
                            }
                            state.tradeOrderHistory = filterOrders(order, state.tradeOrderHistory);
                            state.conditionalOpenOrders.sort((a, b) =>
                                a.time - b.time > 0 ? -1 : 1,
                            );
                            state.tradeOrderHistory.sort((a, b) => (a.time - b.time > 0 ? -1 : 1));
                            if (
                                order.status === ORDER_STATUS.PARTIALLY_FILLED ||
                                order.status === ORDER_STATUS.FILLED
                            ) {
                                state.toast = {
                                    id: order.clientOrderId,
                                    title:
                                        order.status === ORDER_STATUS.FILLED
                                            ? 'order_filled'
                                            : 'order_partially_filled',
                                    type: 'info',
                                };
                            }
                        });
                        break;
                    case SOCKET_TOPICS.BALANCE:
                        state.assets = state.assets.map((asset) => {
                            const balance = data.find(
                                (balance: SpotBalance) => balance.tokenId === asset.tokenId,
                            );
                            return balance ? { ...asset, ...balance } : asset;
                        });
                        break;
                    default:
                        break;
                }
            }
        },
        orderSettingFetched(state, action) {
            state.loading = false;
            state.orderSetting = action.payload;
        },
        allActiveOrderCancelled(state, action) {
            const response = action.payload;
            if (response.success) {
                state.activeOpenOrders = state.activeOpenOrders.filter(
                    (order: ActiveOrder) => order.symbolId !== response.symbolId,
                );
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'orders_cancelled',
                    type: 'success',
                };
            } else {
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'orders_cancelled_failure',
                    type: 'error',
                };
            }
            state.loading = false;
        },
        allConditionalOrderCancelled(state, action) {
            const response = action.payload;
            if (response.success) {
                state.conditionalOpenOrders = state.conditionalOpenOrders.filter(
                    (order: ConditionalOrder) => order.symbolId !== response.symbolId,
                );
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'orders_cancelled',
                    type: 'success',
                };
            } else {
                state.toast = {
                    id: new Date().getTime().toString(),
                    title: 'orders_cancelled_failure',
                    type: 'error',
                };
            }
            state.loading = false;
        },
    },
});

function filterOrders(order: ActiveOrder | TradeOrder, orders: TradeOrder[] | ActiveOrder[]) {
    const orderExist = orders.some((currentOrder) => currentOrder.orderId === order.orderId);
    if (orderExist) {
        return orders.map((currentOrder) => {
            if (currentOrder.orderId === order.orderId) {
                return order;
            }
            return currentOrder;
        });
    } else {
        return [order as TradeOrder, ...orders];
    }
}
